import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, Eye, EyeOff, Star, Upload, FileDown, Pin } from "lucide-react";
import { jsPDF } from "jspdf";
import { api, formatApiError, fmtPrice, applyTheme } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ProjectForm, { emptyProject } from "../components/admin/ProjectForm";

const TABS = ["Boas", "Projects", "Genetics", "Posts", "Subscribers", "Inquiries", "Social", "Theme"];
const FONTS = ["Syne", "Cinzel", "Playfair Display", "Space Grotesk", "Outfit", "Inter"];
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

const emptyBoa = {
  name: "", sex: "Female", price: 0, year: "2024", weight: "", status: "available",
  featured: false, hero: false, live: false, description: "", images: "", morphs: "", het: "",
  sire: "", dam: "", sire_id: "", dam_id: "", sire_morphs: "", dam_morphs: "", litter_date: "", notes: "",
};
const emptyMorph = { name: "", trait_type: "Recessive", category: "Base", description: "", appearance: "", accent: "#00F260", image: "" };
const emptySocial = { platform: "youtube", title: "", url: "", active: true };

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs text-neutral-500 uppercase tracking-wider">{label}</span>
    <input {...props} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500 transition-colors" />
  </label>
);

const GeneSelect = ({ label, options, value, onChange, testid }) => {
  const arr = toArr(value);
  const add = (name) => { if (name && !arr.includes(name)) onChange([...arr, name].join(", ")); };
  const remove = (name) => onChange(arr.filter((x) => x !== name).join(", "));
  return (
    <div className="block">
      <span className="text-xs text-neutral-500 uppercase tracking-wider">{label}</span>
      <select data-testid={testid} value="" onChange={(e) => { add(e.target.value); e.target.value = ""; }}
        className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500 transition-colors">
        <option value="">+ Add gene…</option>
        {options.filter((o) => !arr.includes(o)).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {arr.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {arr.map((m) => (
            <span key={m} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-500/40 text-emerald-700">
              {m}
              <button type="button" onClick={() => remove(m)} className="text-emerald-700/70 hover:text-emerald-900 leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Boas");
  const [boas, setBoas] = useState([]);
  const [morphs, setMorphs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [socials, setSocials] = useState([]);
  const [boaForm, setBoaForm] = useState(null);
  const [morphForm, setMorphForm] = useState(null);
  const [socialForm, setSocialForm] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState(null);
  const [settings, setSettings] = useState({ display_font: "Syne", body_font: "Outfit", scale: 1 });
  const [husbForm, setHusbForm] = useState({ kind: "shed", date: "", note: "" });
  const [projects, setProjects] = useState([]);
  const [projectForm, setProjectForm] = useState(null);
  const [boaProjectLink, setBoaProjectLink] = useState({ project_id: "", role: "Supporting Animal" });
  const [optimizing, setOptimizing] = useState(false);
  const saveSettings = async (e) => { e.preventDefault(); try { const { data } = await api.put("/settings", settings); applyTheme(data); toast.success("Theme saved & applied."); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const optimizePhotos = async () => {
    setOptimizing(true);
    const t = toast.loading("Optimizing uploaded photos…");
    try {
      let shrunk = 0, savedKb = 0, remaining = 1, guard = 0;
      while (remaining > 0 && guard < 200) {
        const { data } = await api.post("/admin/recompress-images?limit=15");
        shrunk += data.shrunk; savedKb += Math.max(0, data.kb_before - data.kb_after); remaining = data.remaining; guard += 1;
      }
      toast.success(shrunk ? `Optimized ${shrunk} photo(s), saved ~${savedKb} KB.` : "All photos already optimized.", { id: t });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Optimization failed.", { id: t }); }
    finally { setOptimizing(false); }
  };

  const loadAll = () => {
    api.get("/admin/boas").then((r) => setBoas(r.data)).catch(() => api.get("/boas").then((r) => setBoas(r.data)));
    api.get("/morphs").then((r) => setMorphs(r.data));
    api.get("/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/admin/socials").then((r) => setSocials(r.data)).catch(() => {});
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  // Lock the background page scroll while any edit popup is open — leaves a single scrollbar (the modal's).
  const anyModalOpen = !!(boaForm || morphForm || socialForm || postForm || projectForm);
  useEffect(() => {
    document.body.style.overflow = anyModalOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [anyModalOpen]);

  const doLogout = async () => { nav("/", { replace: true }); await logout(); };

  const toggleLive = async (b) => {
    try {
      await api.patch(`/boas/${b.id}/live`, { live: !b.live });
      toast.success(!b.live ? `${b.name} is now live.` : `${b.name} hidden from site.`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleFeatured = async (b) => {
    try {
      await api.patch(`/boas/${b.id}/featured`, { live: !b.featured });
      toast.success(!b.featured ? `${b.name} added to Boas of the Month.` : `${b.name} removed from Boas of the Month.`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSocial = async (s) => {
    try { await api.patch(`/socials/${s.id}/active`, { live: !s.active }); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveSocial = async (e) => {
    e.preventDefault();
    try { await api.post("/socials", socialForm); toast.success("Video added."); setSocialForm(null); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delSocial = async (id) => { await api.delete(`/socials/${id}`); toast.success("Deleted."); loadAll(); };

  const togglePin = async (s) => {
    try { await api.patch(`/socials/${s.id}/pinned`, { live: !s.pinned }); toast.success(s.pinned ? "Unpinned." : "Pinned to top."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const togglePost = async (p) => {
    try { await api.patch(`/posts/${p.id}/publish`, { live: !p.published }); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const savePost = async (e) => {
    e.preventDefault();
    try { await api.post("/posts", postForm); toast.success("Newsletter saved."); setPostForm(null); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delPost = async (id) => {
    try { await api.delete(`/posts/${id}`); toast.success("Deleted."); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadImage = async (file, onDone) => {
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading(`Uploading ${file.name}…`);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.url);
      toast.success(`${file.name} uploaded.`, { id: t });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.", { id: t });
    }
  };

  const openBoa = (b) => {
    if (!b) return setBoaForm({ ...emptyBoa });
    setBoaForm({
      ...b, price: b.price, images: (b.images || []).join(", "), morphs: (b.morphs || []).join(", "),
      het: (b.het || []).join(", "), sire: b.lineage?.sire || "", dam: b.lineage?.dam || "",
      sire_id: b.lineage?.sire_id || "", dam_id: b.lineage?.dam_id || "",
      sire_morphs: (b.lineage?.sire_morphs || []).join(", "), dam_morphs: (b.lineage?.dam_morphs || []).join(", "),
      litter_date: b.lineage?.litter_date || "", notes: b.lineage?.notes || "",
    });
  };

  const saveBoa = async (e) => {
    e.preventDefault();
    const f = boaForm;
    const payload = {
      name: f.name, species: "Boa constrictor", sex: f.sex, price: parseFloat(f.price) || 0,
      year: f.year, weight: f.weight, status: f.status, featured: !!f.featured, hero: !!f.hero,
      description: f.description, images: toArr(f.images), morphs: toArr(f.morphs), het: toArr(f.het),
      lineage: { sire: f.sire, dam: f.dam, sire_id: f.sire_id || "", dam_id: f.dam_id || "", sire_morphs: toArr(f.sire_morphs), dam_morphs: toArr(f.dam_morphs), litter_date: f.litter_date, notes: f.notes },
      live: !!f.live,
    };
    try {
      if (f.id) await api.put(`/boas/${f.id}`, payload);
      else await api.post("/boas", payload);
      toast.success("Saved.");
      setBoaForm(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delBoa = async (id) => { await api.delete(`/boas/${id}`); toast.success("Deleted."); loadAll(); };

  const addHusbandry = async () => {
    if (!boaForm?.id || !husbForm.date) { toast.error("Pick a date first."); return; }
    try {
      const { data } = await api.post(`/boas/${boaForm.id}/husbandry`, husbForm);
      setBoaForm((bf) => ({ ...bf, husbandry: [...(bf.husbandry || []), data] }));
      setHusbForm({ kind: husbForm.kind, date: "", note: "" });
      toast.success("Husbandry entry added.");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delHusbandry = async (eid) => {
    try {
      await api.delete(`/boas/${boaForm.id}/husbandry/${eid}`);
      setBoaForm((bf) => ({ ...bf, husbandry: (bf.husbandry || []).filter((x) => x.id !== eid) }));
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveMorph = async (e) => {
    e.preventDefault();
    try {
      if (morphForm.id) await api.put(`/morphs/${morphForm.id}`, morphForm);
      else await api.post("/morphs", morphForm);
      toast.success("Saved.");
      setMorphForm(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delMorph = async (id) => { await api.delete(`/morphs/${id}`); toast.success("Deleted."); loadAll(); };

  // ---- Breeding projects ----
  const openProject = (p) => {
    if (!p) return setProjectForm({ ...emptyProject });
    setProjectForm({
      ...p,
      animals: (p.animals || []).map((a) => ({ boa_id: a.boa_id, role: a.role, note: a.note || "" })),
      timeline: (p.timeline || []).map((t) => ({ id: t.id, date: t.date || "", title: t.title || "", description: t.description || "", images: t.images || [], video_url: t.video_url || "", boa_ids: t.boa_ids || [], public: t.public !== false })),
      holdbacks: (p.holdbacks || []).map((h) => ({ boa_id: h.boa_id, reason: h.reason || "", observations: h.observations || "", future_role: h.future_role || "", images: h.images || [] })),
      connections: (p.connections || []).map((c) => ({ project_id: c.project_id, note: c.note || "" })),
      phenotype_goals: p.phenotype_goals || [],
      genetics: p.genetics || [],
    });
  };
  const saveProject = async (e) => {
    e.preventDefault();
    const f = projectForm;
    const payload = {
      slug: f.slug, title: f.title, subtitle: f.subtitle, status: f.status, generation: f.generation,
      hero_image: f.hero_image, thesis: f.thesis, story: f.story, why_animals: f.why_animals,
      genetics_text: f.genetics_text, genetics: f.genetics || [], phenotype_goals: f.phenotype_goals || [], learning: f.learning,
      outcomes_text: f.outcomes_text, next_generation: f.next_generation, north_star: f.north_star,
      animals: f.animals, timeline: f.timeline, holdbacks: f.holdbacks, connections: f.connections,
      public: !!f.public, archived: !!f.archived,
    };
    try {
      if (f.id) await api.put(`/projects/${f.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Project saved.");
      setProjectForm(null); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delProject = async (id) => { await api.delete(`/projects/${id}`); toast.success("Deleted."); loadAll(); };

  // ---- Animal <-> project links (from the boa editor; same relational data as the project editor) ----
  const linkBoaToProject = async () => {
    if (!boaProjectLink.project_id || !boaForm?.id) return;
    try {
      await api.post(`/projects/${boaProjectLink.project_id}/animals`, { boa_id: boaForm.id, role: boaProjectLink.role });
      toast.success("Added to project.");
      setBoaProjectLink({ project_id: "", role: "Supporting Animal" });
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unlinkBoaFromProject = async (pid) => {
    try {
      await api.delete(`/projects/${pid}/animals/${boaForm.id}`);
      toast.success("Removed from project.");
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // ---- Husbandry care record PDF ----
  const downloadCareRecord = (b) => {
    const geneStr = (v) => (Array.isArray(v) ? v.map((m) => (typeof m === "string" ? m : m?.name || "")) : toArr(v));
    const morphs = geneStr(b.morphs);
    const het = geneStr(b.het);
    const doc = new jsPDF();
    let y = 18;
    doc.setFontSize(18); doc.text("Quantum Boas — Care Record", 14, y); y += 10;
    doc.setFontSize(12);
    doc.text(`Animal: ${b.name}`, 14, y); y += 7;
    doc.text(`Sex: ${b.sex || "—"}   Year: ${b.year || "—"}   Weight: ${b.weight || "—"}`, 14, y); y += 7;
    if (morphs.length) { doc.text(`Genetics: ${morphs.join(", ")}`, 14, y); y += 7; }
    if (het.length) { doc.text(`Het: ${het.join(", ")}`, 14, y); y += 7; }
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, y); y += 10;

    const kinds = [["shed", "Shed history"], ["feeding", "Feeding history"], ["deepclean", "Deep clean history"]];
    kinds.forEach(([kind, title]) => {
      const rows = (b.husbandry || []).filter((h) => h.kind === kind).sort((a, c) => (c.date || "").localeCompare(a.date || ""));
      doc.setFontSize(13); doc.setFont(undefined, "bold"); doc.text(title, 14, y); y += 7;
      doc.setFontSize(11); doc.setFont(undefined, "normal");
      if (!rows.length) { doc.text("— none logged —", 18, y); y += 7; }
      rows.forEach((h) => {
        if (y > 275) { doc.addPage(); y = 18; }
        doc.text(`• ${h.date || "—"}${h.note ? "  —  " + h.note : ""}`, 18, y); y += 6;
      });
      y += 4;
    });
    doc.save(`care-record-${(b.name || "boa").replace(/\s+/g, "-").toLowerCase()}.pdf`);
  };

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 md:px-10 bg-neutral-50 text-neutral-900">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl">Dashboard</h1>
            <p className="text-neutral-500 text-sm mt-1">Manage your collection, genetics and leads.</p>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/15 text-sm hover:border-qbsecondary hover:text-qbsecondary transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>

        <div className="flex gap-2 border-b border-black/10 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
              className={`px-4 py-3 text-sm relative transition-colors ${tab === t ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-600"}`}>
              {t} <span className="text-neutral-300">({t === "Boas" ? boas.length : t === "Projects" ? projects.length : t === "Genetics" ? morphs.length : t === "Posts" ? posts.length : t === "Subscribers" ? subs.length : t === "Inquiries" ? inquiries.length : t === "Social" ? socials.length : ""})</span>
              {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />}
            </button>
          ))}
        </div>

        {tab === "Boas" && (
          <div>
            <button onClick={() => openBoa(null)} data-testid="add-boa-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qbprimary text-qbbg mb-6 hover:opacity-90 transition-colors">
              <Plus className="w-4 h-4" /> Add boa
            </button>
            <div className="grid gap-3">
              {boas.map((b) => (
                <div key={b.id} className="flex items-center gap-4 p-4 rounded-xl border border-black/10 bg-white">
                  <img src={b.images?.[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-display text-xl">{b.name}</span>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${b.status === "available" ? "text-emerald-600 border-emerald-500/40" : b.status === "reserved" ? "text-amber-400 border-amber-400/40" : b.status === "nfs" ? "text-violet-600 border-violet-500/40" : "text-neutral-400 border-black/15"}`}>{b.status}</span>
                      {!b.live && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-400">draft</span>}
                      {b.featured && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-300">★ Month</span>}</div>
                    <p className="text-xs text-neutral-500 truncate">{(b.morphs || []).join(", ")} · {fmtPrice(b.price)}</p>
                  </div>
                  <button onClick={() => toggleFeatured(b)} data-testid={`toggle-featured-${b.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${b.featured ? "border-amber-400/60 text-amber-300 hover:bg-amber-400/10" : "border-black/15 text-neutral-500 hover:text-neutral-900 hover:border-black/30"}`}>
                    <Star className="w-3.5 h-3.5" /> {b.featured ? "Month ✓" : "Month"}
                  </button>
                  <button onClick={() => toggleLive(b)} data-testid={`toggle-live-${b.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${b.live ? "border-emerald-500/50 text-emerald-600 hover:bg-qbprimary/10" : "border-black/15 text-neutral-500 hover:text-neutral-900 hover:border-black/30"}`}>
                    {b.live ? <><Eye className="w-3.5 h-3.5" /> Live</> : <><EyeOff className="w-3.5 h-3.5" /> Make live</>}
                  </button>
                  <button onClick={() => openBoa(b)} data-testid={`edit-boa-${b.id}`} className="p-2 text-neutral-600 hover:text-emerald-600 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delBoa(b.id)} data-testid={`delete-boa-${b.id}`} className="p-2 text-neutral-600 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Projects" && (
          <div>
            <button onClick={() => openProject(null)} data-testid="add-project-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qbprimary text-qbbg mb-4 hover:opacity-90 transition-colors">
              <Plus className="w-4 h-4" /> Add project
            </button>
            <p className="text-xs text-neutral-500 mb-6">Projects are multi-generation journals connected to your existing animals. Keep statuses honest — only mark breeding, ovulation or litters once they actually happen.</p>
            <div className="grid gap-3">
              {projects.length === 0 && <p className="text-neutral-400">No projects yet.</p>}
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl border border-black/10 bg-white" data-testid={`admin-project-${p.slug}`}>
                  {p.hero_image && <img src={p.hero_image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-display text-xl">{p.title || "Untitled"}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-500/40 text-emerald-700">{p.status}</span>
                      {!p.public && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-500">private</span>}
                      {p.archived && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-black/15 text-neutral-400">archived</span>}</div>
                    <p className="text-xs text-neutral-500 truncate">/projects/{p.slug} · {(p.animals || []).length} animals · {(p.timeline || []).length} journal entries · {(p.holdbacks || []).length} holdbacks</p>
                  </div>
                  <button onClick={() => openProject(p)} data-testid={`edit-project-${p.id}`} className="p-2 text-neutral-600 hover:text-emerald-600 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delProject(p.id)} data-testid={`delete-project-${p.id}`} className="p-2 text-neutral-600 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Genetics" && (
          <div>
            <button onClick={() => setMorphForm({ ...emptyMorph })} data-testid="add-morph-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qbprimary text-qbbg mb-6 hover:opacity-90 transition-colors">
              <Plus className="w-4 h-4" /> Add morph
            </button>
            <div className="grid gap-3">
              {morphs.map((m) => (
                <div key={m.id} className="flex items-center gap-4 p-4 rounded-xl border border-black/10 bg-white">
                  <span className="w-3 h-3 rounded-full" style={{ background: m.accent }} />
                  <div className="flex-1 min-w-0"><span className="font-medium">{m.name}</span>
                    <p className="text-xs text-neutral-500 truncate">{m.trait_type} · {m.description}</p></div>
                  <button onClick={() => setMorphForm(m)} className="p-2 text-neutral-600 hover:text-emerald-600 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delMorph(m.id)} className="p-2 text-neutral-600 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Posts" && (
          <div>
            <button onClick={() => setPostForm({ title: "", body: "", published: true })} data-testid="add-post-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qbprimary text-qbbg mb-6 hover:opacity-90 transition-colors">
              <Plus className="w-4 h-4" /> New newsletter
            </button>
            <div className="grid gap-3">
              {posts.length === 0 && <p className="text-neutral-400">No issues yet.</p>}
              {posts.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl border border-black/10 bg-white">
                  <div className="flex-1 min-w-0"><div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{p.title}</span>
                    {!p.published && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-400">draft</span>}</div>
                    <p className="text-xs text-neutral-500 truncate">{new Date(p.created_at).toLocaleDateString()} · {(p.body || "").slice(0, 80)}</p></div>
                  <button onClick={() => togglePost(p)} data-testid={`toggle-post-${p.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${p.published ? "border-emerald-500/50 text-emerald-600 hover:bg-qbprimary/10" : "border-black/15 text-neutral-500 hover:text-neutral-900"}`}>{p.published ? <><Eye className="w-3.5 h-3.5" /> Live</> : <><EyeOff className="w-3.5 h-3.5" /> Publish</>}</button>
                  <button onClick={() => delPost(p.id)} data-testid={`delete-post-${p.id}`} className="p-2 text-neutral-600 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Theme" && (
          <form onSubmit={saveSettings} data-testid="theme-form" className="max-w-md space-y-5">
            <p className="text-neutral-500 text-sm">Change the fonts and text size across the entire website.</p>
            <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Heading font</span>
              <select data-testid="theme-display-font" value={settings.display_font} onChange={(e) => setSettings({ ...settings, display_font: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm">{FONTS.map((f) => <option key={f}>{f}</option>)}</select></label>
            <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Body font</span>
              <select data-testid="theme-body-font" value={settings.body_font} onChange={(e) => setSettings({ ...settings, body_font: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm">{FONTS.map((f) => <option key={f}>{f}</option>)}</select></label>
            <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Text size: {Math.round((settings.scale || 1) * 100)}%</span>
              <input type="range" min="0.85" max="1.25" step="0.05" value={settings.scale} data-testid="theme-scale" onChange={(e) => setSettings({ ...settings, scale: parseFloat(e.target.value) })} className="mt-2 w-full" /></label>
            <button type="submit" data-testid="save-theme-btn" className="px-6 py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:opacity-90 transition-colors">Save theme</button>

            <div className="pt-6 mt-2 border-t border-black/10">
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Maintenance</p>
              <p className="text-sm text-neutral-500 mb-3">Compress all previously uploaded photos so they load faster. Runs once — new uploads are optimized automatically.</p>
              <button type="button" onClick={optimizePhotos} disabled={optimizing} data-testid="optimize-photos-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/50 text-emerald-700 text-sm hover:bg-emerald-50 transition-colors disabled:opacity-60">
                <FileDown className="w-4 h-4" /> {optimizing ? "Optimizing…" : "Optimize existing photos"}
              </button>
            </div>
          </form>
        )}

        {tab === "Subscribers" && (
          <div className="grid gap-2" data-testid="newsletter-list">
            {subs.length === 0 && <p className="text-neutral-400">No signups yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className="flex justify-between p-4 rounded-lg border border-black/10 bg-white text-sm">
                <span>{s.email}</span><span className="text-neutral-400">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "Inquiries" && (
          <div className="grid gap-3" data-testid="inquiries-list">
            {inquiries.length === 0 && <p className="text-neutral-400">No inquiries yet.</p>}
            {inquiries.map((q) => (
              <div key={q.id} className="p-4 rounded-lg border border-black/10 bg-white">
                <div className="flex justify-between"><span className="font-medium">{q.name} · {q.email}</span>
                  <span className="text-neutral-400 text-xs">{new Date(q.created_at).toLocaleDateString()}</span></div>
                {q.message && <p className="text-sm text-neutral-600 mt-2">{q.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "Social" && (
          <div>
            <button onClick={() => setSocialForm({ ...emptySocial })} data-testid="add-social-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qbprimary text-qbbg mb-6 hover:opacity-90 transition-colors">
              <Plus className="w-4 h-4" /> Add video
            </button>
            <div className="grid gap-3">
              {socials.length === 0 && <p className="text-neutral-400">No videos yet.</p>}
              {socials.map((s) => (
                <div key={s.id} className={`flex items-center gap-4 p-4 rounded-xl border bg-white ${s.pinned ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-black/10"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{s.title || "(untitled)"}</span>
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-black/15 text-neutral-500">{s.platform}</span>
                      {s.pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-600"><Pin className="w-3 h-3" /> pinned</span>}
                      {!s.active && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-400">hidden</span>}</div>
                    <p className="text-xs text-neutral-500 truncate">{s.url}</p>
                  </div>
                  <button onClick={() => togglePin(s)} data-testid={`pin-social-${s.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${s.pinned ? "border-emerald-500/50 text-emerald-600 bg-emerald-50" : "border-black/15 text-neutral-500 hover:text-neutral-900 hover:border-black/30"}`}>
                    <Pin className="w-3.5 h-3.5" /> {s.pinned ? "Pinned" : "Pin"}
                  </button>
                  <button onClick={() => toggleSocial(s)} data-testid={`toggle-social-${s.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${s.active ? "border-emerald-500/50 text-emerald-600 hover:bg-qbprimary/10" : "border-black/15 text-neutral-500 hover:text-neutral-900 hover:border-black/30"}`}>
                    {s.active ? <><Eye className="w-3.5 h-3.5" /> On</> : <><EyeOff className="w-3.5 h-3.5" /> Off</>}
                  </button>
                  <button onClick={() => delSocial(s.id)} data-testid={`delete-social-${s.id}`} className="p-2 text-neutral-600 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Boa form modal */}
      {boaForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={saveBoa} data-testid="boa-form" className="w-full max-w-2xl bg-white border border-black/15 rounded-2xl p-6 md:p-8 my-auto">
            <div className="flex justify-between items-center mb-6"><h2 className="font-display text-2xl">{boaForm.id ? "Edit" : "Add"} boa</h2>
              <button type="button" onClick={() => setBoaForm(null)}><X className="w-5 h-5 text-neutral-600" /></button></div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" data-testid="boa-name" value={boaForm.name} onChange={(e) => setBoaForm({ ...boaForm, name: e.target.value })} required />
              <Field label="Price (USD, 0 = inquire)" data-testid="boa-price" type="number" step="0.01" value={boaForm.price} onChange={(e) => setBoaForm({ ...boaForm, price: e.target.value })} />
              <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Sex</span>
                <select value={boaForm.sex} data-testid="boa-sex" onChange={(e) => setBoaForm({ ...boaForm, sex: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                  {["Female", "Male", "Unknown"].map((x) => <option key={x}>{x}</option>)}</select></label>
              <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Status</span>
                <select value={boaForm.status} data-testid="boa-status" onChange={(e) => setBoaForm({ ...boaForm, status: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                  {["available", "reserved", "sold", "nfs"].map((x) => <option key={x} value={x}>{x === "nfs" ? "NFS (breeder — hidden from shop)" : x}</option>)}</select></label>
              <Field label="Year" data-testid="boa-year" value={boaForm.year} onChange={(e) => setBoaForm({ ...boaForm, year: e.target.value })} />
              <Field label="Weight" data-testid="boa-weight" value={boaForm.weight} onChange={(e) => setBoaForm({ ...boaForm, weight: e.target.value })} />
            </div>
            <label className="block mt-4"><span className="text-xs text-neutral-500 uppercase tracking-wider">Description</span>
              <textarea rows={2} value={boaForm.description} onChange={(e) => setBoaForm({ ...boaForm, description: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary resize-none" /></label>
            <Field label="Image URLs (comma separated)" data-testid="boa-images" value={boaForm.images} onChange={(e) => setBoaForm({ ...boaForm, images: e.target.value })} />
            <label className="inline-flex items-center gap-2 mt-2 text-xs text-emerald-600 cursor-pointer hover:underline">
              <Upload className="w-3.5 h-3.5" /> Upload photos from device
              <input type="file" accept="image/*" multiple data-testid="boa-image-upload" className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); files.forEach((f) => uploadImage(f, (url) => setBoaForm((bf) => ({ ...bf, images: bf.images ? bf.images + ", " + url : url })))); e.target.value = ""; }} />
            </label>
            {toArr(boaForm.images).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="boa-gallery">
                {toArr(boaForm.images).map((url, idx, arr) => (
                  <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-black/15">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
                      <button type="button" onClick={() => { const a = [...arr]; if (idx > 0) { [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; setBoaForm({ ...boaForm, images: a.join(", ") }); } }} className="text-white text-sm px-1">◀</button>
                      <button type="button" onClick={() => { const a = [...arr]; a.splice(idx, 1); setBoaForm({ ...boaForm, images: a.join(", ") }); }} className="text-white text-sm px-1">✕</button>
                      <button type="button" onClick={() => { const a = [...arr]; if (idx < a.length - 1) { [a[idx + 1], a[idx]] = [a[idx], a[idx + 1]]; setBoaForm({ ...boaForm, images: a.join(", ") }); } }} className="text-white text-sm px-1">▶</button>
                    </div>
                    {idx === 0 && <span className="absolute top-0 left-0 bg-qbprimary text-qbbg text-[9px] px-1">cover</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <GeneSelect label="Morphs" testid="boa-morphs" options={morphs.map((m) => m.name)} value={boaForm.morphs} onChange={(v) => setBoaForm({ ...boaForm, morphs: v })} />
              <GeneSelect label="Het traits" testid="boa-het" options={morphs.map((m) => m.name)} value={boaForm.het} onChange={(v) => setBoaForm({ ...boaForm, het: v })} />
            </div>
            <p className="text-xs text-neutral-400 uppercase tracking-wider mt-6 mb-2">Parentage</p>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Sire — link a snake (optional)</span>
                <select data-testid="boa-sire-select" value={boaForm.sire_id || ""} onChange={(e) => { const p = boas.find((x) => x.id === e.target.value); setBoaForm({ ...boaForm, sire_id: e.target.value, ...(p ? { sire: p.name, sire_morphs: (p.morphs || []).join(", ") } : { sire: "", sire_morphs: "" }) }); }} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                  <option value="">— none —</option>
                  {boas.filter((x) => x.id !== boaForm.id).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.sex})</option>)}
                </select></label>
              <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Dam — link a snake (optional)</span>
                <select data-testid="boa-dam-select" value={boaForm.dam_id || ""} onChange={(e) => { const p = boas.find((x) => x.id === e.target.value); setBoaForm({ ...boaForm, dam_id: e.target.value, ...(p ? { dam: p.name, dam_morphs: (p.morphs || []).join(", ") } : { dam: "", dam_morphs: "" }) }); }} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                  <option value="">— none —</option>
                  {boas.filter((x) => x.id !== boaForm.id).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.sex})</option>)}
                </select></label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sire" value={boaForm.sire} onChange={(e) => setBoaForm({ ...boaForm, sire: e.target.value })} />
              <Field label="Dam" value={boaForm.dam} onChange={(e) => setBoaForm({ ...boaForm, dam: e.target.value })} />
              <Field label="Sire morphs" value={boaForm.sire_morphs} onChange={(e) => setBoaForm({ ...boaForm, sire_morphs: e.target.value })} />
              <Field label="Dam morphs" value={boaForm.dam_morphs} onChange={(e) => setBoaForm({ ...boaForm, dam_morphs: e.target.value })} />
              <Field label="Litter date" value={boaForm.litter_date} onChange={(e) => setBoaForm({ ...boaForm, litter_date: e.target.value })} />
              <Field label="Notes" value={boaForm.notes} onChange={(e) => setBoaForm({ ...boaForm, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between mt-6 mb-2">
              <p className="text-xs text-neutral-500 uppercase tracking-wider">Husbandry log — sheds, feedings &amp; deep cleans</p>
              {boaForm.id && (boaForm.husbandry || []).length > 0 && (
                <button type="button" onClick={() => downloadCareRecord(boaForm)} data-testid="download-care-record-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 transition-colors">
                  <FileDown className="w-3.5 h-3.5" /> Download care record (PDF)
                </button>
              )}
            </div>
            {boaForm.id ? (
              <div>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-xs"><span className="block text-neutral-500 mb-1">Type</span>
                    <select value={husbForm.kind} data-testid="husbandry-kind" onChange={(e) => setHusbForm({ ...husbForm, kind: e.target.value })} className="bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500">
                      <option value="shed">Shed</option><option value="feeding">Feeding</option><option value="deepclean">Deep clean</option>
                    </select></label>
                  <label className="text-xs"><span className="block text-neutral-500 mb-1">Date</span>
                    <input type="date" value={husbForm.date} data-testid="husbandry-date" onChange={(e) => setHusbForm({ ...husbForm, date: e.target.value })} className="bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500" /></label>
                  <label className="text-xs flex-1 min-w-[140px]"><span className="block text-neutral-500 mb-1">Note (optional)</span>
                    <input value={husbForm.note} data-testid="husbandry-note" onChange={(e) => setHusbForm({ ...husbForm, note: e.target.value })} placeholder="e.g. rat pup, clean shed" className="w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500" /></label>
                  <button type="button" onClick={addHusbandry} data-testid="husbandry-add-btn" className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition-colors">Add</button>
                </div>
                {(boaForm.husbandry || []).length > 0 && (
                  <div className="mt-3 grid gap-1.5" data-testid="husbandry-list">
                    {[...(boaForm.husbandry || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((h) => (
                      <div key={h.id} className="flex items-center gap-3 text-sm border border-black/10 rounded-lg px-3 py-2">
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${h.kind === "shed" ? "bg-sky-100 text-sky-700" : h.kind === "feeding" ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-violet-700"}`}>{h.kind === "deepclean" ? "deep clean" : h.kind}</span>
                        <span className="font-medium">{h.date || "—"}</span>
                        <span className="text-neutral-500 flex-1 truncate">{h.note}</span>
                        <button type="button" onClick={() => delHusbandry(h.id)} data-testid={`husbandry-del-${h.id}`} className="text-neutral-400 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-400">Save the boa first, then reopen it to start logging shed &amp; feeding dates.</p>
            )}
            {boaForm.id && (
              <div className="mt-6 pt-5 border-t border-black/10" data-testid="boa-projects-section">
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Projects this animal belongs to</p>
                {projects.filter((p) => (p.animals || []).some((a) => a.boa_id === boaForm.id)).map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm border border-black/10 rounded-lg px-3 py-2 mb-1.5" data-testid={`boa-project-row-${p.id}`}>
                    <span className="font-medium flex-1 truncate">{p.title}</span>
                    <span className="text-xs text-neutral-500">{(p.animals.find((a) => a.boa_id === boaForm.id) || {}).role}</span>
                    <button type="button" onClick={() => unlinkBoaFromProject(p.id)} data-testid={`boa-project-remove-${p.id}`} className="text-neutral-400 hover:text-qbsecondary transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <select value={boaProjectLink.project_id} data-testid="boa-project-select" onChange={(e) => setBoaProjectLink({ ...boaProjectLink, project_id: e.target.value })} className="bg-neutral-50 border border-black/15 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-emerald-500">
                    <option value="">Add to project…</option>
                    {projects.filter((p) => !(p.animals || []).some((a) => a.boa_id === boaForm.id)).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                  <select value={boaProjectLink.role} data-testid="boa-project-role" onChange={(e) => setBoaProjectLink({ ...boaProjectLink, role: e.target.value })} className="bg-neutral-50 border border-black/15 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-emerald-500">
                    {["Foundation Female", "Foundation Male", "Possible Sire", "Confirmed Sire", "Dam", "Holdback", "Future Breeder", "Supporting Animal"].map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <button type="button" onClick={linkBoaToProject} data-testid="boa-project-add-btn" className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700 transition-colors">Add</button>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-6 mt-5">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={boaForm.live} data-testid="boa-live-checkbox" onChange={(e) => setBoaForm({ ...boaForm, live: e.target.checked })} /> Live on site</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={boaForm.featured} data-testid="boa-featured-checkbox" onChange={(e) => setBoaForm({ ...boaForm, featured: e.target.checked })} /> Boa of the Month</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={boaForm.hero} onChange={(e) => setBoaForm({ ...boaForm, hero: e.target.checked })} /> Hero</label>
            </div>
            <button type="submit" data-testid="save-boa-btn" className="w-full mt-6 py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:opacity-90 transition-colors">Save boa</button>
          </form>
        </div>
      )}

      {/* Morph form modal */}
      {morphForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={saveMorph} data-testid="morph-form" className="w-full max-w-lg bg-white border border-black/15 rounded-2xl p-6 md:p-8 my-auto">
            <div className="flex justify-between items-center mb-6"><h2 className="font-display text-2xl">{morphForm.id ? "Edit" : "Add"} morph</h2>
              <button type="button" onClick={() => setMorphForm(null)}><X className="w-5 h-5 text-neutral-600" /></button></div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" value={morphForm.name} onChange={(e) => setMorphForm({ ...morphForm, name: e.target.value })} required />
              <label className="block"><span className="text-xs text-neutral-500 uppercase tracking-wider">Trait type</span>
                <select value={morphForm.trait_type} onChange={(e) => setMorphForm({ ...morphForm, trait_type: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                  {["Recessive", "Incomplete Dominant", "Polygenic"].map((x) => <option key={x}>{x}</option>)}</select></label>
              <Field label="Category" value={morphForm.category} onChange={(e) => setMorphForm({ ...morphForm, category: e.target.value })} />
              <Field label="Accent color" type="color" value={morphForm.accent} onChange={(e) => setMorphForm({ ...morphForm, accent: e.target.value })} />
            </div>
            <Field label="Image URL" value={morphForm.image} onChange={(e) => setMorphForm({ ...morphForm, image: e.target.value })} />
            <label className="block mt-4"><span className="text-xs text-neutral-500 uppercase tracking-wider">Description</span>
              <textarea rows={3} value={morphForm.description} onChange={(e) => setMorphForm({ ...morphForm, description: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary resize-none" /></label>
            <Field label="Appearance" value={morphForm.appearance} onChange={(e) => setMorphForm({ ...morphForm, appearance: e.target.value })} />
            <button type="submit" data-testid="save-morph-btn" className="w-full mt-6 py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:opacity-90 transition-colors">Save morph</button>
          </form>
        </div>
      )}

      {/* Social form modal */}
      {socialForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={saveSocial} data-testid="social-form" className="w-full max-w-lg bg-white border border-black/15 rounded-2xl p-6 md:p-8 my-auto">
            <div className="flex justify-between items-center mb-6"><h2 className="font-display text-2xl">Add video</h2>
              <button type="button" onClick={() => setSocialForm(null)}><X className="w-5 h-5 text-neutral-600" /></button></div>
            <label className="block mb-4"><span className="text-xs text-neutral-500 uppercase tracking-wider">Platform</span>
              <select value={socialForm.platform} data-testid="social-platform" onChange={(e) => setSocialForm({ ...socialForm, platform: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary">
                {["youtube", "instagram", "tiktok", "x", "facebook", "vimeo"].map((x) => <option key={x}>{x}</option>)}</select></label>
            <Field label="Title" data-testid="social-title" value={socialForm.title} onChange={(e) => setSocialForm({ ...socialForm, title: e.target.value })} />
            <div className="mt-4"><Field label="Video URL (paste an Instagram reel/post or YouTube link)" data-testid="social-url" value={socialForm.url} onChange={(e) => setSocialForm({ ...socialForm, url: e.target.value })} required /></div>
            <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" checked={socialForm.active} data-testid="social-active-checkbox" onChange={(e) => setSocialForm({ ...socialForm, active: e.target.checked })} /> Visible on site</label>
            <button type="submit" data-testid="save-social-btn" className="w-full mt-6 py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:opacity-90 transition-colors">Save video</button>
          </form>
        </div>
      )}

      {/* Newsletter post modal */}
      {postForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4">
          <form onSubmit={savePost} data-testid="post-form" className="w-full max-w-2xl bg-white border border-black/15 rounded-2xl p-6 md:p-8 my-auto">
            <div className="flex justify-between items-center mb-6"><h2 className="font-display text-2xl">New newsletter</h2><button type="button" onClick={() => setPostForm(null)}><X className="w-5 h-5 text-neutral-600" /></button></div>
            <Field label="Title" data-testid="post-title" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} required />
            <label className="block mt-4"><span className="text-xs text-neutral-500 uppercase tracking-wider">Body</span>
              <textarea rows={8} data-testid="post-body" value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} className="mt-1 w-full bg-neutral-50 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-qbprimary resize-none" /></label>
            <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" checked={postForm.published} data-testid="post-published" onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} /> Send live now</label>
            <button type="submit" data-testid="save-post-btn" className="w-full mt-6 py-3 rounded-full bg-qbprimary text-qbbg font-medium hover:opacity-90 transition-colors">Save &amp; send live</button>
          </form>
        </div>
      )}

      {/* Project editor modal */}
      {projectForm && (
        <ProjectForm form={projectForm} setForm={setProjectForm} boas={boas} projects={projects}
          onSubmit={saveProject} onClose={() => setProjectForm(null)} uploadImage={uploadImage} />
      )}
    </div>
  );
}
